import {
  CustomConfigOption,
  CustomProductAttributeConfig,
} from '../products/types';
import type { Product } from '../products/product-editor/graphql.schema-types';
import { parseProductConfiguration } from '../products/products-front/product-configuration-calculations';

export const filterAttributesByRelations = (
  filtered: Array<CustomProductAttributeConfig>,
  relationsMap: RelationsMapForLookBook,
  filterForPrevLevel: Array<
    Array<
      CustomProductAttributeConfig & {
        productId: string;
        organizationId: string;
      }
    >
  >,
  filtersFromRelatedProducts: Array<
    Array<
      CustomProductAttributeConfig & {
        productId: string;
        organizationId: string;
      }
    >
  >,
  currentIndex: number
) => {
  const attributesToRemove = [];

  filtered.forEach((visibleElement, visibleElementIndex) => {
    // if (visibleElementIndex === 0) {
    //   return;
    // }
    if (visibleElement.relations?.length) {
      if (['list', 'size', 'decoration'].includes(visibleElement.type)) {
        const matchingOptions = visibleElement.options.filter(
          (singleOptionToCheck) => {
            if (visibleElementIndex === currentIndex) {
              return isOptionVisibleInRelatedProducts(
                singleOptionToCheck,
                relationsMap,
                filterForPrevLevel,
                visibleElement.mustHaveRelationForMixer
              );
            }

            return isOptionVisibleInRelatedProducts(
              singleOptionToCheck,
              relationsMap,
              filtersFromRelatedProducts,
              visibleElement.mustHaveRelationForMixer
            );
          }
        );
        if (matchingOptions.length === 0) {
          attributesToRemove.push(visibleElement.customId);
        } else {
          filtered[visibleElementIndex] = {
            ...filtered[visibleElementIndex],
            options: matchingOptions,
          };
        }
      } else {
        if (
          !shouldAttributeBeVisibleInRelatedProducts(
            visibleElement,
            relationsMap,
            filtersFromRelatedProducts
          )
        ) {
          attributesToRemove.push(visibleElement.customId);
        }
      }
    }
  });
  if (attributesToRemove.length) {
    const roRemove = [...new Set(attributesToRemove)];
    filtered = filtered.filter(({ customId }) => !roRemove.includes(customId));
  }
};
export const isOptionVisibleInRelatedProducts = (
  option: CustomConfigOption,
  relationsMap: RelationsMapForLookBook,
  relatedProducts: Array<
    Array<
      CustomProductAttributeConfig & {
        productId: string;
        organizationId: string;
      }
    >
  >,
  isAttributeForced = false
): boolean => {
  if (
    !relationsMap ||
    (!relationsMap[option.configId] && !isAttributeForced) ||
    relatedProducts.length === 0
  ) {
    return true;
  }

  return !!relatedProducts.filter(
    (attributes) =>
      !!attributes.filter(
        ({ options, organizationId, productId, customId }) => {
          const relationExists =
            relationsMap[option.configId] &&
            relationsMap[option.configId][organizationId] &&
            relationsMap[option.configId][organizationId][productId]
              ?.map(({ element }) => element)
              .includes(customId);
          if (!relationExists) {
            return false;
          }
          const optioExists =
            typeof options.find(({ configId }) =>
              relationsMap[option.configId][organizationId][productId]
                .map(({ value }) => value)
                .includes(configId)
            ) !== 'undefined';
          return !!options.length && relationExists && optioExists;
        }
      ).length
  ).length;
};

export const shouldAttributeBeVisibleInRelatedProducts = (
  attribute: CustomProductAttributeConfig,
  relationsMap: RelationsMapForLookBook,
  relatedProducts: Array<
    Array<
      CustomProductAttributeConfig & {
        productId: string;
        organizationId: string;
      }
    >
  >
): boolean => {
  if (
    !relationsMap ||
    !relationsMap[attribute.customId] ||
    relatedProducts.length === 0
  ) {
    return true;
  }
  return !!relatedProducts.filter(
    (attributes) =>
      !!attributes.filter(
        ({ organizationId, productId, customId }) =>
          relationsMap[attribute.customId][organizationId] &&
          relationsMap[attribute.customId][organizationId][productId] &&
          customId ===
            relationsMap[attribute.customId][organizationId][productId][0]
              .element
      ).length
  ).length;
};
export const calculateFiltersFromRelatedProducts = (
  products: Array<Product>,
  mappedValues: any,
  currentValue,
  productsToRemove
) => {
  return products
    .map((product) => {
      const filteredProduct: Array<
        CustomProductAttributeConfig & {
          organizationId: string;
          productId: string;
        }
      > = [];

      const currentProductValue = {
        ...mappedValues[product.productId],
      };
      parseProductConfiguration(
        // @ts-ignore
        [...product.config],
        undefined,
        { ...currentProductValue },
        filteredProduct,
        false,
        true,
        true
      );

      const entries = Object.entries(currentProductValue);
      if (
        entries.length === 0 &&
        Object.values(currentValue).filter((v) => v !== '').length > 0
      ) {
        productsToRemove.push(product.productId);
      }

      entries.forEach(([attribute, value]) => {
        const elementWithValueExists = filteredProduct.find(
          ({ customId }) => attribute === customId
        );
        const attributeExistsAtAll = (
          product.config as any as Array<CustomProductAttributeConfig>
        ).find(({ customId }) => attribute === customId);
        if (!elementWithValueExists && attributeExistsAtAll) {
          console.log(
            'REMOVING ',
            product.name,
            'CAUSE ',
            attributeExistsAtAll.label[0].text,
            ' is missing'
          );
          productsToRemove.push(product.productId);
          return;
        }
        // console.log(elementWithValueExists);
        if (!elementWithValueExists) {
          return;
        }
        switch (elementWithValueExists.type) {
          case 'list':
          case 'decoration':
          case 'size':
            const optionExists = elementWithValueExists.options.find(
              ({ configId }) => configId === value
            );
            if (!optionExists && value) {
              productsToRemove.push(product.productId);
            }
            break;
          case 'counter':
            const { min, max } = elementWithValueExists.counterConfig;
            if (
              min > currentProductValue[elementWithValueExists.customId] ||
              max < currentProductValue[elementWithValueExists.customId]
            ) {
              productsToRemove.push(product.productId);
            }
            break;
        }
      });
      return filteredProduct.map((attribute) => ({
        ...attribute,
        organizationId: product.organizationId,
        productId: product.productId,
      }));
    })
    .map((attributes) =>
      attributes.filter(
        ({ productId }) => !productsToRemove.includes(productId)
      )
    );
};
export const calculateProductsInPlay = (
  labsInPlay: Array<string>,
  productsMap: Record<string, Array<Product & { labId: string }>>,
  currentValue: Record<string, string | number>,
  filtered: Array<CustomProductAttributeConfig>,
  relationsMap: RelationsMapForLookBook,
  currentIndex: number,
  allowEmptyForCurrentIndex = false
): {
  mappedValues: Record<string, Record<string, string>>;
  productsInPlay: Array<Product>;
} => {
  const productsMapObjects = new Map<Product & { labId: string }, number>();
  const mappedValues = {};
  if (!relationsMap) {
    return { productsInPlay: [], mappedValues: {} };
  }
  for (const currentElement in currentValue) {
    if (!currentValue.hasOwnProperty(currentElement)) {
      return;
    }

    const elementValue = currentValue[currentElement];

    const hasRelations = !!relationsMap[elementValue];
    // console.log(hasRelations);
    if (hasRelations) {
      for (const labId in relationsMap[currentElement]) {
        if (labsInPlay.includes(labId)) {
          const products = productsMap[labId];
          const targetProductIds = Object.keys(
            relationsMap[currentElement][labId]
          );
          productsMapObjects.set(
            products.find(({ productId }) =>
              targetProductIds.includes(productId)
            ),
            1
          );
        }
        const currentRelation =
          relationsMap[currentValue[currentElement]][labId];
        for (const sourceProduct in currentRelation) {
          if (currentRelation.hasOwnProperty(sourceProduct)) {
            if (typeof currentRelation[sourceProduct] !== 'undefined') {
              if (!mappedValues[sourceProduct]) {
                mappedValues[sourceProduct] = {};
              }

              relationsMap[currentValue[currentElement]][labId][
                sourceProduct
              ].forEach(({ element, value }) => {
                mappedValues[sourceProduct][element] = value;
              });
            }
          }
        }
      }
    } else if (relationsMap[currentElement]) {
      for (const labId in relationsMap[currentElement]) {
        if (labsInPlay.includes(labId)) {
          const products = productsMap[labId];
          const targetProductIds = Object.keys(
            relationsMap[currentElement][labId]
          );

          products
            .filter(({ productId }) => targetProductIds.includes(productId))
            .forEach((product) => productsMapObjects.set(product, 1));

          const currentRelation = relationsMap[currentElement][labId];
          for (const sourceProduct in currentRelation) {
            if (
              !currentRelation.hasOwnProperty(sourceProduct) ||
              (allowEmptyForCurrentIndex === false && elementValue === '') ||
              currentIndex === Object.keys(currentValue).indexOf(currentElement)
            ) {
              continue;
            }
            if (typeof currentRelation[sourceProduct] !== 'undefined') {
              const productFound = productsMap[labId]?.find(
                ({ productId }) => productId === sourceProduct
              );
              if (!productFound) {
                continue;
              }
              const productInPlayAttributes = JSON.parse(
                productFound.config
              ) as Array<CustomProductAttributeConfig>;
              const attributeInPlay = productInPlayAttributes.find(
                ({ customId }) =>
                  customId === currentRelation[sourceProduct][0].element
              );
              switch (attributeInPlay.type) {
                case 'list':
                case 'size':
                case 'decoration':
                case 'text':
                  if (
                    typeof elementValue !== 'string'
                    // && (elementValue as any as boolean) !== false
                  ) {
                    continue;
                  }
                  break;
                case 'boolean':
                  if (typeof elementValue !== 'boolean') {
                    continue;
                  }
                  break;
                case 'counter':
                  if (typeof elementValue !== 'number') {
                    continue;
                  }
                  break;
              }
              if (!mappedValues[sourceProduct]) {
                mappedValues[sourceProduct] = {};
              }

              mappedValues[sourceProduct][
                currentRelation[sourceProduct][0].element
              ] = elementValue;
            }
          }
        }
      }
    }
  }
  const productsInPlay = [];
  productsMapObjects.forEach((value, key) => {
    const missingRequiredAttributes = filtered.filter(
      (
        { customId, mustHaveRelationForMixer, type, label, counterConfig },
        i
      ) => {
        const relationCheck =
          mustHaveRelationForMixer &&
          (['list', 'size', 'decoration'].includes(type)
            ? !relationsMap[currentValue[customId]] ||
              !relationsMap[currentValue[customId]][key.labId] ||
              !relationsMap[currentValue[customId]][key.labId][key.productId]
            : typeof relationsMap[customId] !== 'undefined' &&
              (!relationsMap[customId][key.labId] ||
                !relationsMap[customId][key.labId][key.productId]));
        let counterCheck = true;
        if (type === 'counter') {
          const productConfig = JSON.parse(
            key.config
          ) as CustomProductAttributeConfig[];
          try {
            const match = relationsMap[customId][key.labId][key.productId].map(
              ({ element }) => element
            );

            const relatedCounterConfig = productConfig.find(({ customId }) =>
              match.includes(customId)
            );

            const counterValue = Number(currentValue[customId]);
            counterCheck = Number.isFinite(counterValue) &&
              relatedCounterConfig.counterConfig.min <= counterValue &&
              relatedCounterConfig.counterConfig.max >= counterValue;
            // console.log(
            //   key.name,
            //   label,
            //   relatedCounterConfig,
            //   currentValue[customId],
            //   counterCheck
            // );
          } catch (e) {}
        }
        return currentValue[customId] && relationCheck && counterCheck;
      }
    );
    if (missingRequiredAttributes.length === 0) {
      productsInPlay.push({ ...key, config: JSON.parse(key.config) });
    }
  });
  return {
    productsInPlay,
    mappedValues: JSON.parse(JSON.stringify(mappedValues)),
  };
};

export const buildRelationsMapForLookBook = (
  config: Array<CustomProductAttributeConfig>
): RelationsMapForLookBook => {
  const relationsMap: RelationsMapForLookBook = {};

  config
    .filter(({ relations }) => !!relations?.length)
    .forEach(({ customId, relations, options }) => {
      if (!relationsMap[customId]) {
        relationsMap[customId] = {};
      }
      relations.forEach(({ labId, type, productId, value, parentId, id }) => {
        if (!relationsMap[customId][labId]) {
          relationsMap[customId][labId] = {};
        }
        if (!relationsMap[customId][labId][productId]) {
          relationsMap[customId][labId][productId] = [];
        }

        if (type === 'Attribute') {
          relationsMap[customId][labId][productId].push({
            element: id,
          });
        }
      });
      if (options.length) {
        options
          .filter((option) => option.relations?.length)
          .forEach((singleOption) => {
            singleOption.relations.forEach(
              ({ labId, productId, parentId, value, id }) => {
                if (!relationsMap[singleOption.configId]) {
                  relationsMap[singleOption.configId] = {};
                }
                if (!relationsMap[singleOption.configId][labId]) {
                  relationsMap[singleOption.configId][labId] = {};
                }
                if (!relationsMap[singleOption.configId][labId][productId]) {
                  relationsMap[singleOption.configId][labId][productId] = [];
                }

                relationsMap[singleOption.configId][labId][productId].push({
                  element: parentId,
                  value,
                });
              }
            );
          });
      }
    });
  return relationsMap;
};

export interface RelationsMapForLookBook {
  [x: string]: {
    [x: string]: {
      [x: string]: Array<{
        element: string;
        value?: any;
      }>;
    };
  };
}
